/**
 * CreditsScreen - Shows game credits and acknowledgements.
 * Covers task 10.5 (credits screen).
 */

import * as GUI from "babylonjs-gui";

export class CreditsScreen {
  private _texture: GUI.AdvancedDynamicTexture;
  private _container: GUI.Rectangle;

  constructor(onClose: () => void) {
    this._texture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("creditsScreen");

    this._container = new GUI.Rectangle("creditsBg");
    this._container.width = "100%";
    this._container.height = "100%";
    this._container.thickness = 0;
    this._container.background = "rgba(0,0,0,0.92)";
    this._container.isVisible = false;
    this._texture.addControl(this._container);

    // Title
    const title = new GUI.TextBlock("creditsTitle", "⚽ AMN FOOTBALL");
    title.color = "#00ff88";
    title.fontSize = 42;
    title.fontWeight = "900";
    title.fontFamily = "Orbitron, sans-serif";
    title.top = "-300px";
    this._container.addControl(title);

    const version = new GUI.TextBlock("version", "Version 1.0.0");
    version.color = "#666666";
    version.fontSize = 12;
    version.fontFamily = "Orbitron, sans-serif";
    version.top = "-255px";
    this._container.addControl(version);

    // Credits content
    const creditsStack = new GUI.StackPanel("creditsStack");
    creditsStack.width = "500px";
    creditsStack.isVertical = true;
    creditsStack.top = "20px";
    this._container.addControl(creditsStack);

    const credits = [
      { role: "GAME DEVELOPMENT", name: "AMN Football Team" },
      { role: "3D GRAPHICS & MODELS", name: "Babylon.js Engine" },
      { role: "PHYSICS ENGINE", name: "Cannon.js" },
      { role: "UI FRAMEWORK", name: "Babylon.js GUI" },
      { role: "AUDIO ENGINE", name: "Web Audio API" },
      { role: "FONT", name: "Orbitron (Google Fonts)" },
      { role: "3D CHARACTER MODELS", name: "Custom Blender Models" },
      { role: "STADIUM DESIGN", name: "Procedural Generation" },
      { role: "SPECIAL THANKS", name: "The Open Source Community" },
    ];

    credits.forEach(({ role, name }) => {
      const row = new GUI.StackPanel(`credit_${role}`);
      row.isVertical = false;
      row.height = "38px";
      row.width = "500px";
      creditsStack.addControl(row);

      const roleText = new GUI.TextBlock(`r_${role}`, role);
      roleText.color = "#aaaaaa";
      roleText.fontSize = 11;
      roleText.fontFamily = "Orbitron, sans-serif";
      roleText.width = "250px";
      roleText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_RIGHT;
      row.addControl(roleText);

      const sep = new GUI.TextBlock(`s_${role}`, "  —  ");
      sep.color = "#444444";
      sep.fontSize = 11;
      sep.fontFamily = "Orbitron, sans-serif";
      sep.width = "50px";
      row.addControl(sep);

      const nameText = new GUI.TextBlock(`n_${role}`, name);
      nameText.color = "white";
      nameText.fontSize = 11;
      nameText.fontWeight = "700";
      nameText.fontFamily = "Orbitron, sans-serif";
      nameText.width = "250px";
      nameText.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_LEFT;
      row.addControl(nameText);
    });

    // License note
    const license = new GUI.TextBlock("license",
      "AMN Football is an open-source project built with free and offline-capable tools.\n" +
      "All assets are either procedurally generated or created by the development team."
    );
    license.color = "#555555";
    license.fontSize = 10;
    license.fontFamily = "Orbitron, sans-serif";
    license.height = "50px";
    license.top = "280px";
    license.textWrapping = GUI.TextWrapping.WordWrap;
    license.width = "500px";
    license.textHorizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    this._container.addControl(license);

    // Close button
    const closeBtn = GUI.Button.CreateSimpleButton("creditClose", "CLOSE");
    closeBtn.width = "200px";
    closeBtn.height = "50px";
    closeBtn.color = "#00ff88";
    closeBtn.cornerRadius = 4;
    closeBtn.thickness = 1;
    closeBtn.background = "rgba(17, 85, 204, 0.8)";
    closeBtn.fontSize = 18;
    closeBtn.fontWeight = "700";
    closeBtn.fontFamily = "Orbitron, sans-serif";
    closeBtn.top = "340px";
    closeBtn.onPointerUpObservable.add(() => {
      this.hide();
      onClose();
    });
    this._container.addControl(closeBtn);
  }

  show(): void {
    this._container.isVisible = true;
  }

  hide(): void {
    this._container.isVisible = false;
  }

  dispose(): void {
    this._texture.dispose();
  }
}
